/**
 * O token de acesso do parceiro (15 min) e o socket — D3, passo 19 (PR 2).
 *
 * Com o polling pausado pelo socket vivo ninguém renova o token (a renovação só acontece num 401 do `portalApi` ou no `initAuth`;
 * mapa A7). Duas frentes, as duas daqui:
 *  - o servidor ACEITA o socket e fecha com 4001 quando o token vence ou é recusado (PR 1, Decisão 3: o navegador lê o código):
 *    `decisaoAoFechar` renova e reconecta;
 *  - `garantirTokenFresco` roda ANTES de cada tentativa e renova se faltam menos de 60 s: depois de o computador dormir evita um
 *    handshake desperdiçado e o blip de `ws` (o socket abre e fecha em seguida) que dispararia a refeitura do sino.
 * A renovação é a do próprio portal (`portalAuthApi.refresh`, que grava o access token novo). Uma por vez; recusada (401/403) = a sessão do
 * parceiro acabou: mesmo fim do interceptor do `portalApi` (limpa e vai ao login do portal). Sem rede NÃO desloga.
 */
import { portalAuthApi, portalStorage } from "@/services/portalApi";

export const MARGEM_DO_TOKEN_MS = 60_000;

export type ResultadoDaRenovacao = "renovado" | "recusado" | "sem_rede";

/** O `exp` do JWT em milissegundos, ou `null` se o token não é legível. */
export function expiraEm(token: string | null | undefined): number | null {
  if (!token) return null;
  try {
    const carga = token.split(".")[1];
    if (!carga) return null;
    const base64 = carga.replace(/-/g, "+").replace(/_/g, "/");
    const exp = JSON.parse(atob(base64 + "=".repeat((4 - (base64.length % 4)) % 4))).exp;
    return typeof exp === "number" ? exp * 1000 : null;
  } catch {
    return null;
  }
}

/** O fim do interceptor do `portalApi` quando o refresh é recusado: a sessão do parceiro acabou. */
export function irParaOLoginDoPortal(): void {
  portalStorage.clear();
  if (window.location.pathname !== "/portal/login") {
    window.location.href = "/portal/login";
  }
}

let renovando: Promise<ResultadoDaRenovacao> | null = null;

export function renovarTokenDoPortal(
  aoRecusar: () => void = irParaOLoginDoPortal
): Promise<ResultadoDaRenovacao> {
  if (renovando) return renovando;
  renovando = (async (): Promise<ResultadoDaRenovacao> => {
    const refresh = portalStorage.getRefreshToken();
    if (!refresh) {
      aoRecusar();
      return "recusado";
    }
    try {
      await portalAuthApi.refresh(refresh);
      return "renovado";
    } catch (erro) {
      const status = (erro as { response?: { status?: number } })?.response?.status;
      if (status === 401 || status === 403) {
        aoRecusar();
        return "recusado";
      }
      return "sem_rede";
    }
  })().finally(() => {
    renovando = null;
  });
  return renovando;
}

/** O token tem vida de sobra para abrir o socket? Se não, renova. `false` = não deu (o transporte tenta de novo com recuo). */
export async function garantirTokenFresco(
  margemMs: number = MARGEM_DO_TOKEN_MS,
  agora: number = Date.now()
): Promise<boolean> {
  const exp = expiraEm(portalStorage.getAccessToken());
  if (exp !== null && exp - agora > margemMs) return true;
  return (await renovarTokenDoPortal()) === "renovado";
}
